import React from "react";
import {
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  Repeat,
} from "lucide-react";

interface Transaction {
  id: string;
  createdAt: string;
  transactionRef: string;
  currency: string;
  refereeCode: string;
  FullName: string;
  type: string;
  transactionType: string;
  amount: string;
  status: string;
}

interface TransactionTableProps {
  data?: {
    data: Transaction[];
    page: number;
    pageSize: number;
    total: string;
    totalPages: number;
  };
  isLoading: boolean;
  page: number;
  onPageChange: (page: number) => void;
}

export const toSentenceCase = (text: string | null | undefined) => {
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getStatusColor = (status: string) => {
  if (!status) return "bg-gray-100 text-gray-800";

  switch (status.toLowerCase()) {
    case "successful":
      return "bg-emerald-100 text-emerald-800"; // Green for success
    case "failed":
      return "bg-red-100 text-red-800"; // Red for failure
    case "pending":
      return "bg-amber-100 text-amber-800"; // Yellow for pending
    case "processing":
      return "bg-blue-100 text-blue-800"; // Blue for processing
    case "completing":
      return "bg-indigo-100 text-indigo-800"; // Indigo for completing
    case "cancelled":
      return "bg-gray-200 text-gray-700"; // Gray for cancelled
    case "refunded":
      return "bg-purple-100 text-purple-800"; // Purple for refunded
    default:
      return "bg-gray-100 text-gray-800"; // Default fallback
  }
};

const getTypeIcon = (type: string) => {
  if (!type) return null;

  switch (type.toLowerCase()) {
    case "debit":
      return <ArrowUp className="h-4 w-4 text-red-500" />;
    case "credit":
      return <ArrowDown className="h-4 w-4 text-green-500" />;
    case "swap":
      return <Repeat className="h-4 w-4 text-blue-500" />;
    default:
      return null;
  }
};

const TransactionTable = ({
  data,
  isLoading,
  page,
  onPageChange,
}: TransactionTableProps) => {
  const handlePrevPage = () => {
    onPageChange(Math.max(1, page - 1));
  };

  const handleNextPage = () => {
    if (data && page < data.totalPages) {
      onPageChange(page + 1);
    }
  };

  if (isLoading) {
    return <div className="flex justify-center p-4">Loading...</div>;
  }

  const columns = [
    { key: "sn", label: "S/N", align: "left" },
    // { key: "id", label: "Transaction ID", align: "left" },
    { key: "createdAt", label: "Date", align: "left" },
    { key: "transactionRef", label: "Reference", align: "left" },
    { key: "FullName", label: "Full Name", align: "left" },
    { key: "type", label: "Transaction Type", align: "left" },
    { key: "refereeCode", label: "Referee Code", align: "left" },
    { key: "currency", label: "Currency", align: "left" },
    { key: "amount", label: "Amount", align: "left" },
    { key: "status", label: "Status", align: "center" },
  ];
  const formatCurrency = (
    amount: string,
    currency: string,
    decimals: number = 2
  ) => {
    const currencySymbols: Record<string, string> = {
      USD: "$",
      NGN: "₦",
      EUR: "€",
      GBP: "£",
      USDT: "USDT",
      USDC: "USDC",
    };
    const symbol = currencySymbols[currency] || currency;
    return `${symbol}${Number(amount).toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })}`;
  };

  return (
    <div className="bg-white rounded-lg w-full">
      <div className="overflow-x-auto">
        <table className="appearance-none bg-white min-w-full  mb-6 ">
          <thead className="bg-[#fff] appearance-none">
            <tr className="border-b bg-gray-50">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`font-normal text-sm text-primary py-4  whitespace-nowrap px-4 border-r border-slate-50 ${
                    column.align === "right"
                      ? "text-right"
                      : column.align === "center"
                      ? "text-center"
                      : "text-left"
                  }`}
                >
                  <span className="text-[#5C6F7F] font-bold text-[12px] leading-[18px]">
                    {" "}
                    {column.label}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="mt-3 pt-3 w-full space-y-8 border-r border-slate-50">
            {data?.data.map((transaction, index) => (
              <tr
                key={transaction.id}
                className="appearance-none my-4 border border-slate-50 even:bg-white odd:bg-slate-100"
              >
                <td className="py-4 px-4 text-sm text-[#202223]">
                  {index + 1 + (page - 1) * (data?.pageSize || 0)}
                </td>
                {/* <td className="py-4 px-4 text-sm text-[#202223]">
                  {transaction.id}
                </td> */}
                <td className="py-4 px-4 text-sm text-[#202223]">
                  {formatDate(transaction.createdAt)}
                </td>
                <td className="py-4 px-4 text-sm text-[#202223]">
                  {transaction.transactionRef}
                </td>
                <td className="py-4 px-4 text-sm text-[#202223]">
                  {transaction.FullName}
                </td>
                <td className="py-4 px-4 text-sm text-[#202223] flex items-center gap-2">
                  {getTypeIcon(transaction.type)}
                  {toSentenceCase(transaction.type)}
                </td>
                <td className="py-4 px-4 text-sm text-[#202223]">
                  {transaction.refereeCode}
                </td>
                <td className="py-4 px-4 text-sm text-[#202223]">
                  {transaction.currency}
                </td>

                <td className="py-4 px-4 text-sm text-[#202223] text-left tabular-nums">
                  {formatCurrency(transaction.amount, transaction.currency)}
                </td>
                <td className="py-4 px-4 text-sm text-center">
                  <span
                    className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${getStatusColor(
                      transaction.status
                    )}`}
                  >
                    {toSentenceCase(transaction.status)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-500">
          Page {page} of {data?.totalPages} ({data?.total} total records)
        </div>
        <div className="flex gap-2">
          <button
            className={`flex items-center px-3 py-1 text-sm border rounded-md ${
              page <= 1
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
            onClick={handlePrevPage}
            disabled={page <= 1}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </button>
          <button
            className={`flex items-center px-3 py-1 text-sm border rounded-md ${
              !data || page >= data.totalPages
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
            onClick={handleNextPage}
            disabled={!data || page >= data.totalPages}
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionTable;
